import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { FUNDING_DOCUMENT_CATEGORIES } from '../../utils/funding/constants';
import { emptyFundingDocumentRequest } from '../../services/funding/fundingDocumentsService';

const FIELD_CLASS =
  'w-full rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2.5 text-sm text-[var(--fg-text)] outline-none ring-[var(--fg-accent)] focus:ring-2 dark:bg-[rgba(11,31,24,0.65)]';

const FundingDocumentRequestModal = ({
  open,
  onOpenChange,
  onSubmit,
  saving = false,
  error = null,
}) => {
  const [values, setValues] = useState(emptyFundingDocumentRequest());

  useEffect(() => {
    if (open) {
      setValues(emptyFundingDocumentRequest());
    }
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!values.category) return;
    await onSubmit({
      category: values.category,
      status: values.status || 'Requested',
      notes: values.notes?.trim() || null,
      file_name: values.file_name?.trim() || null,
      file_url: values.file_url?.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Funding Document</DialogTitle>
          <DialogDescription>
            Add a document requirement. File upload integration is a follow-up — link fields are available now.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              Category
            </label>
            <select
              value={values.category}
              onChange={(event) => setValues((prev) => ({ ...prev, category: event.target.value }))}
              className={FIELD_CLASS}
              required
            >
              <option value="">Select category</option>
              {FUNDING_DOCUMENT_CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              File name (optional)
            </label>
            <input
              value={values.file_name}
              onChange={(event) => setValues((prev) => ({ ...prev, file_name: event.target.value }))}
              className={FIELD_CLASS}
              placeholder="statement-jan.pdf"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              File URL (optional)
            </label>
            <input
              value={values.file_url}
              onChange={(event) => setValues((prev) => ({ ...prev, file_url: event.target.value }))}
              className={FIELD_CLASS}
              placeholder="https://..."
            />
            <p className="mt-1 text-xs text-[var(--fg-muted)]">
              Direct upload to FundElite storage will be wired in a follow-up slice.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
              Notes
            </label>
            <textarea
              rows={3}
              value={values.notes}
              onChange={(event) => setValues((prev) => ({ ...prev, notes: event.target.value }))}
              className={FIELD_CLASS}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]" disabled={saving}>
              {saving ? 'Saving...' : 'Request document'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FundingDocumentRequestModal;
